import { BufferAttribute, type BufferGeometry } from "three";

/** Vento a partir do qual o vértice é copa (o mesmo corte do sumiço perto, `NEAR_FADE_LEAF_WIND`, e da malha só-folha). */
export const CROWN_WIND = 0.3;
/** Precisão (m) para juntar vértices no mesmo lugar (a geometria sem índice repete cada canto por triângulo). */
const WELD = 1e-4;

/**
 * Normais de copa (Fase 14, as copas redondas da referência, só shader): em cada vértice de folha (`wind` ≥
 * `CROWN_WIND`), a direção do centro da moita dele até ele. As moitas são os pedaços ligados da copa (os
 * triângulos de folha que dividem um canto); o centro é a média dos cantos da moita. O shader mistura essa
 * normal com a da face para a luz (a copa sombreia como uma bola facetada; as linhas das arestas ficam). Fora
 * da copa, (0, 0, 0): o shader fica com a face. Pede a geometria sem índice; sem `wind` ou sem folha, nada.
 */
export function crownNormals(geometry: BufferGeometry): Float32Array | undefined {
  const position = geometry.getAttribute("position");
  const wind = geometry.getAttribute("wind");
  if (!position || !wind || geometry.index) return undefined;
  const n = position.count;
  const leaf = (i: number) => wind.getX(i) >= CROWN_WIND;
  // União-busca pelos cantos: o mesmo lugar é o mesmo vértice, e um triângulo de folha junta os seus.
  const parent = Int32Array.from({ length: n }, (_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]!]!;
      i = parent[i]!;
    }
    return i;
  };
  const union = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  };
  const seen = new Map<string, number>();
  let leaves = 0;
  for (let i = 0; i < n; i++) {
    if (!leaf(i)) continue;
    leaves++;
    const key = `${Math.round(position.getX(i) / WELD)},${Math.round(position.getY(i) / WELD)},${Math.round(position.getZ(i) / WELD)}`;
    const other = seen.get(key);
    if (other === undefined) seen.set(key, i);
    else union(i, other);
  }
  if (leaves === 0) return undefined;
  for (let t = 0; t + 2 < n; t += 3) {
    if (leaf(t) && leaf(t + 1)) union(t, t + 1);
    if (leaf(t + 1) && leaf(t + 2)) union(t + 1, t + 2);
  }
  const sums = new Map<number, [number, number, number, number]>();
  for (let i = 0; i < n; i++) {
    if (!leaf(i)) continue;
    const r = find(i);
    const s = sums.get(r) ?? [0, 0, 0, 0];
    s[0] += position.getX(i);
    s[1] += position.getY(i);
    s[2] += position.getZ(i);
    s[3]++;
    sums.set(r, s);
  }
  const out = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    if (!leaf(i)) continue;
    const s = sums.get(find(i))!;
    const dx = position.getX(i) - s[0] / s[3];
    const dy = position.getY(i) - s[1] / s[3];
    const dz = position.getZ(i) - s[2] / s[3];
    const len = Math.hypot(dx, dy, dz);
    // No centro exato (moita de um ponto só), para cima.
    if (len < 1e-6) out[i * 3 + 1] = 1;
    else {
      out[i * 3] = dx / len;
      out[i * 3 + 1] = dy / len;
      out[i * 3 + 2] = dz / len;
    }
  }
  return out;
}

/**
 * A geometria com o atributo `aCrown` (as normais de copa) sempre que ela tem `wind`: zeros sem copa. O
 * shader do vento lê o atributo, e um atributo ausente leria o último valor genérico daquela posição, de
 * outro programa (o three não refaz os padrões a cada desenho). Devolve a mesma geometria.
 */
export function withCrownNormals(geometry: BufferGeometry): BufferGeometry {
  const wind = geometry.getAttribute("wind");
  if (!wind) return geometry;
  geometry.setAttribute("aCrown", new BufferAttribute(crownNormals(geometry) ?? new Float32Array(wind.count * 3), 3));
  return geometry;
}
